import { useAdminStorageUsage } from "../../hooks/use-admin.js";
import { Skeleton } from "../../components/skeleton.js";

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const val = bytes / Math.pow(1024, i);
  return `${val.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function pctColor(pct: number): string {
  if (pct >= 80) return "#d32f2f";
  if (pct >= 50) return "#f57c00";
  return "#388e3c";
}

function UsageBar({ pct, label }: { pct: number; label: string }) {
  const clamped = Math.min(pct, 100);
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 4,
          fontSize: 13,
        }}
      >
        <span>{label}</span>
        <span style={{ color: pctColor(pct), fontWeight: 600 }}>
          {pct.toFixed(2)}%
        </span>
      </div>
      <div
        style={{
          background: "#e0e0e0",
          borderRadius: 4,
          height: 10,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${clamped}%`,
            height: "100%",
            background: pctColor(pct),
            borderRadius: 4,
            transition: "width 0.3s ease",
          }}
        />
      </div>
    </div>
  );
}

export function AdminStoragePage() {
  const { data, isLoading, error } = useAdminStorageUsage();

  return (
    <div className="product-page">
      <h1 className="page-title">R2 Storage Usage</h1>

      {isLoading && <Skeleton lines={3} />}

      {error && (
        <div className="error-text" style={{ color: "#d32f2f", padding: 16 }}>
          Failed to load storage usage data.
        </div>
      )}

      {data && (
        <>
          <section className="product-section">
            <h2 className="section-title">Status</h2>
            <div className="stat-grid">
              <div className="stat-item">
                <span className="stat-item__value">
                  {data.r2Enabled ? "Active" : "Disabled"}
                </span>
                <span className="stat-item__label">R2 Storage</span>
              </div>
              <div className="stat-item">
                <span className="stat-item__value">
                  {data.totalStorageGB.toFixed(3)} GB
                </span>
                <span className="stat-item__label">Total stored</span>
              </div>
              <div className="stat-item">
                <span className="stat-item__value">10 GB</span>
                <span className="stat-item__label">Free tier limit</span>
              </div>
            </div>
          </section>

          <section className="product-section">
            <h2 className="section-title">
              Free Tier Usage — {data.currentMonth.monthKey}
            </h2>
            <UsageBar
              pct={data.currentMonth.freeTier.storagePct}
              label={`Storage: ${formatBytes(data.currentMonth.writes.bytes)} of 10 GB`}
            />
            <UsageBar
              pct={data.currentMonth.freeTier.writeOpsPct}
              label={`Class A (writes): ${formatCount(data.currentMonth.writes.count)} of 1M`}
            />
            <UsageBar
              pct={data.currentMonth.freeTier.readOpsPct}
              label={`Class B (reads): ${formatCount(data.currentMonth.reads.count)} of 10M`}
            />
          </section>

          <section className="product-section">
            <h2 className="section-title">Current Month Breakdown</h2>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Operation</th>
                  <th className="text-right">Count</th>
                  <th className="text-right">Bytes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Writes (uploads)</td>
                  <td className="text-right">
                    {formatCount(data.currentMonth.writes.count)}
                  </td>
                  <td className="text-right">
                    {formatBytes(data.currentMonth.writes.bytes)}
                  </td>
                </tr>
                <tr>
                  <td>Reads (downloads)</td>
                  <td className="text-right">
                    {formatCount(data.currentMonth.reads.count)}
                  </td>
                  <td className="text-right">
                    {formatBytes(data.currentMonth.reads.bytes)}
                  </td>
                </tr>
                <tr>
                  <td>Deletes</td>
                  <td className="text-right">
                    {formatCount(data.currentMonth.deletes.count)}
                  </td>
                  <td className="text-right">
                    {formatBytes(data.currentMonth.deletes.bytes)}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          {data.recentMonths.length > 1 && (
            <section className="product-section">
              <h2 className="section-title">Monthly History</h2>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="text-right">Total Ops</th>
                    <th className="text-right">Total Bytes</th>
                    <th className="text-right">Storage %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentMonths.map((m) => (
                    <tr key={m.monthKey}>
                      <td>{m.monthKey}</td>
                      <td className="text-right">
                        {formatCount(m.totalOperations)}
                      </td>
                      <td className="text-right">
                        {formatBytes(m.totalBytes)}
                      </td>
                      <td
                        className="text-right fw-600"
                        style={{
                          color: pctColor(m.freeTier.storagePct),
                        }}
                      >
                        {m.freeTier.storagePct.toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}
    </div>
  );
}
